import { MOWER_DEFS } from '../../content/mowers';
import {
  duelLawn,
  mazeMatchLawn,
  partyDuelLawn,
  partyMazeLawn,
  partyRoundupLawn,
  partySoccerLawn,
  roundupMatchLawn,
  soccerMatchLawn,
  type DuelLawn,
  type RaceSize,
} from '../../gen/modes';
import type { RoomMode } from './live';
import { placesFrom } from './race';
import { decodeIntent } from '../../sim/replay';
import type { SimEvent } from '../../sim/events';
import { Simulation } from '../../sim/simulation';
import { SIM_RULES, type SimRules } from '../../sim/simRules';
import { simRulesFor } from '../rulesVersion';

/**
 * Head to head's shared rules for a match (docs/HEAD_TO_HEAD.md): the room judges a match by
 * replaying it with these, and each phone rebuilds its own copy the same way, so the two can only
 * differ in which turns they know yet.
 */

/**
 * The first rules version with head to head as it plays now (version 4: dizzy bumps in the side):
 * both phones must have it (and alike rules).
 */
export const DUEL_RULES_FROM = 4;

/**
 * The first rules version with soccer matches as they play now (docs/SOCCER_MATCH.md): version 8
 * shortened a LONG match to 70 seconds, inside the Zero-Turn's gas (version 7's 75 ran it dry).
 */
export const SOCCER_RULES_FROM = 8;

/** The first rules version with sheep round-up matches (docs/ROUNDUP_MATCH.md). */
export const ROUNDUP_RULES_FROM = 8;

/** The first rules version with hedge maze matches (docs/MAZE_MATCH.md). */
export const MAZE_RULES_FROM = 10;

/** The first rules version that can play a room of this mode (0: any). */
export function rulesFromFor(mode: RoomMode): number {
  if (mode === 'roundup') return ROUNDUP_RULES_FROM;
  if (mode === 'maze') return MAZE_RULES_FROM;
  if (mode === 'soccer') return SOCCER_RULES_FROM;
  return mode === 'duel' ? DUEL_RULES_FROM : 0;
}

/** A turn as a match keeps it: `[tick, intent code]`, the tick it applies on. */
export type MatchTurn = readonly [number, number];

const lawns = new Map<string, DuelLawn>();

/**
 * A match's lawn, made once per seed, size and mode (building one takes a few milliseconds): head
 * to head's mirrored lawn, a soccer pitch, a round-up's paddock or a hedge maze.
 */
export function matchLawn(
  seed: string,
  size: RaceSize,
  mode: RoomMode = 'duel',
  /** How many play (docs/PARTY.md): three or four get a party's lawn. */
  players = 2,
): DuelLawn {
  const key = `${mode}:${size}:${seed}:${players}`;
  let lawn = lawns.get(key);
  if (!lawn) {
    lawn =
      players > 2
        ? partyLawn(seed, size, mode, players)
        : mode === 'soccer'
          ? soccerMatchLawn(seed, size)
          : mode === 'roundup'
            ? roundupMatchLawn(seed, size)
            : mode === 'maze'
              ? mazeMatchLawn(seed, size)
              : duelLawn(seed, size);
    if (lawns.size > 20) lawns.clear();
    lawns.set(key, lawn);
  }
  return lawn;
}

/**
 * The sim rules a match plays by: its room's (the inviter's version, `simRulesFor`); a room from
 * before version 9 did not say, and played by version 8's.
 */
export function matchRulesFor(roomRules: number | null | undefined): Readonly<SimRules> {
  return simRulesFor(roomRules ?? 8);
}

/**
 * Whether a game on `rules` can play a match in a room on `roomRules`: a newer game plays any older
 * room's sim rules, an older one only rules that play a match alike. No match drives the Fairway
 * (its side reels are the only difference from version 9 on), so those rules do not count.
 */
export function canPlayMatchRules(rules: number, roomRules: number): boolean {
  const a = simRulesFor(rules);
  const b = simRulesFor(roomRules);
  return (
    rules >= roomRules ||
    (a.softSmallCritters === b.softSmallCritters &&
      a.turnIntoSheep === b.turnIntoSheep &&
      a.turnIntoBall === b.turnIntoBall)
  );
}

/** Modes a party of three or four can play so far (docs/PARTY.md: the rest are being built). */
export const PARTY_MODES: readonly RoomMode[] = ['duel', 'maze', 'roundup', 'soccer'];

/** A party's lawn (three or four players) in a mode that has one. */
function partyLawn(seed: string, size: RaceSize, mode: RoomMode, players: number): DuelLawn {
  if (mode === 'duel') return partyDuelLawn(seed, size, players);
  if (mode === 'maze') return partyMazeLawn(seed, size, players);
  if (mode === 'roundup') return partyRoundupLawn(seed, size, players);
  if (mode === 'soccer' && players === 4) return partySoccerLawn(seed, size);
  throw new Error(`no party lawn for ${mode} yet`);
}

/** The first rules version with parties of up to four (docs/PARTY.md). */
export const PARTY_RULES_FROM = 11;

/** The first rules version with each mode for a party of three or four (docs/PARTY.md). */
export const PARTY_MODE_RULES_FROM: Partial<Record<RoomMode, number>> = {
  duel: 11,
  maze: 12,
  roundup: 13,
  soccer: 14,
};

/** The first rules version that can open or join a party room in this mode. */
export function partyRulesFrom(mode: RoomMode): number {
  return PARTY_MODE_RULES_FROM[mode] ?? PARTY_RULES_FROM;
}

/**
 * Each seat's place in a match as it stands (1 first; the same score shares a place): by grass,
 * goals, sheep penned, or (a maze) tiles from the middle. Mowers that broke down come after those
 * still going, and seats in `out` (left or went silent) after both.
 */
export function matchPlaces(sim: Simulation, out: readonly number[] = []): number[] {
  const seats = Array.from({ length: sim.seats }, (_, s) => s);
  const level = sim.level;
  const scores = level.maze
    ? [...sim.mazeDistances]
    : level.roundup
      ? [...sim.penCounts]
      : level.soccer
        ? [...sim.soccerGoals]
        : seats.map((s) => sim.seatTiles(s));
  // Still going, then broken down, then gone. (Two against two: a team shares its place, so only
  // leaving counts.)
  const tiers = seats.map((s) =>
    out.includes(s) ? 2 : !sim.teams && sim.seatStatus(s) !== 'running' ? 1 : 0,
  );
  return placesFrom(scores, level.maze ? 'lower' : 'higher', tiers);
}

/** A new match on a lawn: both loaners at their corners, nothing played yet. */
export function startMatch(lawn: DuelLawn, rules: Readonly<SimRules> = SIM_RULES): Simulation {
  return new Simulation(lawn.def.sim, MOWER_DEFS[lawn.loaner], {}, [], rules);
}

/**
 * Plays `sim` on to `untilTick` (or the match's end) with each seat's turns, in tick order: a turn
 * at tick t is given to the sim just before the step that makes tick t. Turns for ticks it has
 * already passed are left out (a phone's guesses are rebuilt from the start, never patched).
 */
export function playMatchTo(
  sim: Simulation,
  turns: readonly (readonly MatchTurn[])[],
  untilTick: number,
  /** Hears each step's events (the phone draws them); without it they are dropped. */
  onEvents?: (events: SimEvent[]) => void,
): Simulation {
  const next = turns.map((list) => {
    let i = 0;
    while (i < list.length && list[i]![0] <= sim.tick) i++;
    return i;
  });
  while (sim.status === 'running' && sim.tick < untilTick) {
    turns.forEach((list, seat) => {
      for (; next[seat]! < list.length && list[next[seat]!]![0] === sim.tick + 1; next[seat]!++) {
        sim.enqueueFor(seat, decodeIntent(list[next[seat]!]![1]));
      }
    });
    const events = sim.step();
    onEvents?.(events);
  }
  return sim;
}

/** A match replayed from the start with these turns, to `untilTick` or its end. */
export function playMatch(
  lawn: DuelLawn,
  turns: readonly (readonly MatchTurn[])[],
  untilTick: number,
  rules: Readonly<SimRules> = SIM_RULES,
): Simulation {
  return playMatchTo(startMatch(lawn, rules), turns, untilTick);
}

/**
 * Where the room stamps a turn sent for tick `t` (docs/HEAD_TO_HEAD.md): no earlier than
 * `maxLate` ticks behind its clock (`nowTick`), nor than the seat's last turn (turns stay in
 * order), nor later than `maxAhead` ahead.
 */
export function stampTurn(
  t: number,
  nowTick: number,
  lastTick: number,
  maxLate: number,
  maxAhead: number,
): number {
  return Math.min(Math.max(t, nowTick - maxLate, lastTick, 1), nowTick + maxAhead);
}
